import React, { memo, ReactNode, useEffect, useState } from 'react';
import Div from '../Div';
import Slider, { Settings } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import SlickArrow from '@/components/Icons/SlickArrow';
import ProgressBar from './ProgressBar';
import classNames from 'classnames';

export interface SlickSliderProps {
  className?: string;
  data: any[];
  settings?: Settings;
  dataTestId?: string;
  render: (item: any, index: number) => ReactNode;
  showProgress?: boolean;
  progressBarStyle?: any;
}

interface SliderSettings {
  dots: boolean;
  infinite: boolean;
  speed: number;
  slidesToShow: number;
  slidesToScroll: number;
  arrows: boolean;
}

const SlickSlider = ({
  className,
  data,
  settings,
  render,
  showProgress = false,
  progressBarStyle,
}: SlickSliderProps) => {
  const [progress, setProgress] = useState(0);
  const [slideToShow, setSlideToShow] = useState(0);
  const settingsData: SliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    centerMode: false,
    prevArrow: (
      <Div className="slick-prev">
        <Div className="rotate-180">
          <SlickArrow />
        </Div>
      </Div>
    ),
    nextArrow: (
      <Div className="slick-next">
        <Div>
          <SlickArrow />
        </Div>
      </Div>
    ),
    afterChange: (current: any) => {
      setProgress((100 / (data.length - slideToShow)) * current);
    },
    ...settings,
  };

  const setSlides = () => {
    if (window.innerHeight <= 1280 && window.innerWidth > 1000) {
      setSlideToShow(settingsData.slidesToShow);
    } else if (window.innerWidth <= 1000 && window.innerWidth > 650) {
      setSlideToShow(2);
    } else if (window.innerWidth <= 650) {
      setSlideToShow(1);
    }
  };

  useEffect(() => {
    if (showProgress) {
      setSlides();
      window.addEventListener('resize', () => setSlides());
    }
    return () => {
      window.removeEventListener('resize', () => setSlides());
    };
  }, []);

  return (
    <>
      <Slider {...settingsData} className={className}>
        {data?.map((item, index) => (
          <Div key={item.id} className="h-full">
            {render(item, index)}
          </Div>
        ))}
      </Slider>
      {showProgress && (
        <Div className={classNames('w-9/12	lg:w-11/12', progressBarStyle)}>
          <ProgressBar progressBarPercentage={String(progress + '%')} />
        </Div>
      )}
    </>
  );
};

export default memo(SlickSlider);
